import seedPayload from '../../data/stations.seed.json'
import { getFirebaseIdToken } from './firebase'
import type {
  Answer,
  ApiErrorBody,
  Section,
  Station,
  StationImage,
  StationStatus,
  StationType,
  Subquestion,
} from './types'

const stationTypes: readonly StationType[] = ['image', 'clinical', 'instrument', 'mixed']
const stationStatuses: readonly StationStatus[] = ['draft', 'needs_review', 'published', 'archived']
const API_PAGE_SIZE = 100

export type ApiSource = 'api' | 'seed'

export interface ApiPayloadResult<T> {
  data: T
  source: ApiSource
  warning?: string
}

export interface StationsQuery {
  sectionId?: string
  /** Optional server filters used by learner and admin-compatible views. */
  search?: string
  status?: StationStatus | 'all'
  includeArchived?: boolean
}

interface SeedPayload {
  sections?: unknown[]
  stations?: unknown[]
}

interface StationListPayload {
  total?: unknown
  hasMore?: unknown
}

interface StationMetadata {
  questionCount?: number
  mediaCount?: number
}

type LearnerStation = Station & StationMetadata

const seed = seedPayload as SeedPayload

function cloneDeep<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function toDisplayError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message
  return 'تعذّر قراءة استجابة الخادم.'
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : Boolean(error && typeof error === 'object' && 'name' in error && (error as { name?: unknown }).name === 'AbortError')
}

function safeString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function safeNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length === 0) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function firstDefined(source: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key]
  }
  return undefined
}

function parseJsonArray(value: unknown): unknown[] | undefined {
  if (Array.isArray(value)) return value
  if (typeof value !== 'string' || value.trim().length === 0) return undefined
  try {
    const parsed = JSON.parse(value) as unknown
    return Array.isArray(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

/** Accept both API envelopes and the importer JSON shape. */
function parsePayloadArray(payload: unknown, preferredKeys: readonly string[] = []): unknown[] | null {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return null

  const source = payload as Record<string, unknown>
  for (const key of [...preferredKeys, 'data', 'items', 'sections', 'stations']) {
    const candidate = source[key]
    if (Array.isArray(candidate)) return candidate
  }
  return null
}

function unwrapStationPayload(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload
  const source = payload as Record<string, unknown>
  if (source.station && typeof source.station === 'object') return source.station
  if (source.data && typeof source.data === 'object' && !Array.isArray(source.data)) return source.data
  return payload
}

function normalizeSection(raw: unknown): Section | null {
  if (!raw || typeof raw !== 'object') return null
  const source = raw as Record<string, unknown>
  const id = safeString(firstDefined(source, 'id', 'sectionId', 'section_id'))
  const name = safeString(firstDefined(source, 'name', 'title'))
  if (!id || !name) return null

  const stationIds = Array.isArray(source.stationIds) ? source.stationIds : []
  const countValue = firstDefined(source, 'stationCount', 'station_count')
  return {
    id,
    name,
    slug: safeString(source.slug) ?? id,
    order: safeNumber(firstDefined(source, 'order', 'sortOrder', 'sort_order'), 0),
    stationCount: countValue === undefined ? stationIds.length || undefined : safeNumber(countValue, 0),
  }
}

function normalizeImage(raw: unknown, fallbackIndex: number): StationImage | null {
  if (!raw || typeof raw !== 'object') return null
  const source = raw as Record<string, unknown>
  const key = safeString(firstDefined(source, 'url', 'src', 'key', 'objectKey', 'path'))
  if (!key) return null

  const id = safeString(source.id) ?? safeString(source.assetId) ?? `image-${fallbackIndex}`
  const width = safeNumber(firstDefined(source, 'width', 'widthPx'), NaN)
  const height = safeNumber(firstDefined(source, 'height', 'heightPx'), NaN)
  return {
    id,
    key,
    alt: safeString(firstDefined(source, 'alt', 'altText', 'description')),
    width: Number.isFinite(width) ? width : undefined,
    height: Number.isFinite(height) ? height : undefined,
  }
}

function normalizeAnswer(raw: unknown, fallbackIndex: number): Answer | null {
  if (!raw || typeof raw !== 'object') return null
  const source = raw as Record<string, unknown>
  const body = safeString(firstDefined(source, 'body', 'content', 'text', 'answer'))
  if (!body) return null

  return {
    id: safeString(source.id) ?? `answer-${fallbackIndex}`,
    body,
    order: safeNumber(firstDefined(source, 'order', 'sortOrder', 'sort_order'), fallbackIndex),
  }
}

function normalizeSubquestion(raw: unknown, fallbackIndex: number): Subquestion | null {
  if (!raw || typeof raw !== 'object') return null
  const source = raw as Record<string, unknown>
  const prompt = safeString(firstDefined(source, 'prompt', 'question'))
  if (!prompt) return null

  const answerSource = firstDefined(source, 'answers', 'answer')
  const answers = Array.isArray(answerSource)
    ? answerSource
        .map((answer, index) => normalizeAnswer(answer, index + 1))
        .filter((answer): answer is Answer => Boolean(answer))
        .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id))
    : []

  return {
    id: safeString(source.id) ?? `question-${fallbackIndex}`,
    prompt,
    order: safeNumber(firstDefined(source, 'order', 'sortOrder', 'sort_order'), fallbackIndex),
    answers,
  }
}

function normalizeTags(value: unknown): string[] | undefined {
  const values = parseJsonArray(value) ?? (typeof value === 'string' ? value.split(',') : undefined)
  if (!values) return undefined
  const tags = values
    .map((tag) => (typeof tag === 'string' || typeof tag === 'number' ? String(tag).trim() : ''))
    .filter(Boolean)
  return tags.length > 0 ? Array.from(new Set(tags)) : undefined
}

function normalizeStation(raw: unknown): LearnerStation | null {
  if (!raw || typeof raw !== 'object') return null
  const source = raw as Record<string, unknown>
  const id = safeString(firstDefined(source, 'id', 'stationId', 'station_id'))
  const sectionId = safeString(firstDefined(source, 'sectionId', 'section_id', 'section'))
  const title = safeString(firstDefined(source, 'title', 'name'))
  if (!id || !sectionId || !title) return null

  const rawType = safeString(source.type)
  const type: StationType = stationTypes.includes(rawType as StationType)
    ? (rawType as StationType)
    : 'clinical'
  const rawStatus = safeString(source.status)
  const status: StationStatus = stationStatuses.includes(rawStatus as StationStatus)
    ? (rawStatus as StationStatus)
    : 'published'

  const imageSource = firstDefined(source, 'images', 'media')
  const images = Array.isArray(imageSource)
    ? imageSource
        .map((image, index) => normalizeImage(image, index + 1))
        .filter((image): image is StationImage => Boolean(image))
    : []
  const questionSource = firstDefined(source, 'subquestions', 'questions')
  const subquestions = Array.isArray(questionSource)
    ? questionSource
        .map((question, index) => normalizeSubquestion(question, index + 1))
        .filter((question): question is Subquestion => Boolean(question))
        .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id))
    : []

  const questionCountValue = firstDefined(source, 'questionCount', 'question_count', 'subquestionCount')
  const mediaCountValue = firstDefined(source, 'mediaCount', 'media_count')
  return {
    id,
    sectionId,
    title,
    scenario: safeString(firstDefined(source, 'scenario', 'context')),
    type,
    status,
    order: safeNumber(firstDefined(source, 'order', 'sortOrder', 'sort_order'), 0),
    source: safeString(source.source),
    images,
    subquestions,
    tags: normalizeTags(source.tags),
    ...(questionCountValue === undefined ? {} : { questionCount: safeNumber(questionCountValue, subquestions.length) }),
    ...(mediaCountValue === undefined ? {} : { mediaCount: safeNumber(mediaCountValue, images.length) }),
  }
}

function normalizeSections(payload: unknown): Section[] {
  const list = parsePayloadArray(payload, ['sections'])
  if (!list) return []
  return list
    .map(normalizeSection)
    .filter((section): section is Section => Boolean(section))
    .sort((left, right) => left.order - right.order || left.name.localeCompare(right.name, 'ar'))
}

function normalizeStations(payload: unknown): LearnerStation[] {
  const list = parsePayloadArray(payload, ['stations'])
  if (!list) return []
  return list
    .map(normalizeStation)
    .filter((station): station is LearnerStation => Boolean(station))
    .sort((left, right) => left.order - right.order || left.title.localeCompare(right.title, 'ar') || left.id.localeCompare(right.id))
}

const SEED_SECTIONS: Section[] = normalizeSections({ sections: seed.sections ?? [] })
const SEED_STATIONS: LearnerStation[] = normalizeStations({ stations: seed.stations ?? [] })

function withStationCounts(sections: Section[], stations: readonly Station[]): Section[] {
  const counts = stations.reduce<Record<string, number>>((result, station) => {
    result[station.sectionId] = (result[station.sectionId] ?? 0) + 1
    return result
  }, {})
  return sections.map((section) => ({
    ...section,
    stationCount: counts[section.id] ?? section.stationCount ?? 0,
  }))
}

async function loadPayload<T>(path: string, signal?: AbortSignal): Promise<T> {
  const headers = new Headers({ accept: 'application/json' })
  const token = await getFirebaseIdToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(path, { headers, signal })
  if (!response.ok) {
    const error = new Error(`تعذر تحميل البيانات من الخادم (رمز ${response.status}).`) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.toLowerCase().includes('json')) throw new Error('وصل رد غير متوقع من الخادم.')
  return (await response.json()) as T
}

function canUseSeedFallback(error: unknown): boolean {
  const status = error && typeof error === 'object' && 'status' in error
    ? Number((error as { status?: unknown }).status)
    : 0
  return import.meta.env.DEV && status !== 401 && status !== 403
}

async function fetchListWithFallback<T>(
  path: string,
  parse: (payload: unknown) => T,
  fallback: T,
  signal?: AbortSignal,
): Promise<ApiPayloadResult<T>> {
  try {
    const parsed = parse(await loadPayload<unknown>(path, signal))
    if (Array.isArray(parsed) && parsed.length === 0) throw new Error('لا يوجد محتوى متاح حاليًا.')
    return { data: parsed, source: 'api' }
  } catch (error) {
    if (isAbortError(error)) throw error
    if (!canUseSeedFallback(error)) throw error
    return { data: cloneDeep(fallback), source: 'seed', warning: toDisplayError(error) }
  }
}

function queryString(query: StationsQuery, page: number): string {
  const params = new URLSearchParams({ page: String(page), pageSize: String(API_PAGE_SIZE) })
  const sectionId = query.sectionId?.trim()
  if (sectionId) params.set('sectionId', sectionId)
  const search = query.search?.trim()
  if (search) params.set('search', search)
  if (query.status) params.set('status', query.status)
  if (query.includeArchived) params.set('includeArchived', '1')
  return params.toString()
}

function payloadTotal(payload: unknown): number | undefined {
  if (!payload || typeof payload !== 'object') return undefined
  const total = safeNumber((payload as StationListPayload).total, NaN)
  return Number.isFinite(total) && total >= 0 ? total : undefined
}

function payloadHasMore(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false
  return (payload as StationListPayload).hasMore === true
}

function filterSeedStations(query: StationsQuery): LearnerStation[] {
  let stations = SEED_STATIONS
  const sectionId = query.sectionId?.trim()
  if (sectionId) stations = stations.filter((station) => station.sectionId === sectionId)
  if (query.status && query.status !== 'all') stations = stations.filter((station) => station.status === query.status)
  if (query.search?.trim()) {
    const search = query.search.trim().toLocaleLowerCase('ar')
    stations = stations.filter((station) => [station.title, station.scenario, station.source]
      .some((value) => value?.toLocaleLowerCase('ar').includes(search)))
  }
  if (!query.includeArchived) stations = stations.filter((station) => station.status !== 'archived')
  return stations
}

export function getDemoSections(): Section[] {
  return cloneDeep(withStationCounts(SEED_SECTIONS, SEED_STATIONS))
}

export function getDemoStations(): Station[] {
  return cloneDeep(SEED_STATIONS)
}

export async function getSections(signal?: AbortSignal): Promise<ApiPayloadResult<Section[]>> {
  return fetchListWithFallback('/api/sections', normalizeSections, getDemoSections(), signal)
}

export async function getStations(
  query: StationsQuery = {},
  signal?: AbortSignal,
): Promise<ApiPayloadResult<Station[]>> {
  const fallback = filterSeedStations(query)
  const firstPath = `/api/stations?${queryString(query, 1)}`
  try {
    const firstPayload = await loadPayload<unknown>(firstPath, signal)
    const stations = normalizeStations(firstPayload)
    if (stations.length === 0) throw new Error('لا توجد محطات متاحة حاليًا.')

    const total = payloadTotal(firstPayload)
    let allStations = stations
    let page = 1
    while ((payloadHasMore(firstPayload) || (total !== undefined && allStations.length < total)) && page < 100) {
      page += 1
      const nextPayload = await loadPayload<unknown>(`/api/stations?${queryString(query, page)}`, signal)
      const nextStations = normalizeStations(nextPayload)
      if (nextStations.length === 0) break
      allStations = [...allStations, ...nextStations]
      if (!payloadHasMore(nextPayload) && (payloadTotal(nextPayload) === undefined || allStations.length >= (payloadTotal(nextPayload) ?? 0))) break
    }

    const unique = Array.from(new Map(allStations.map((station) => [station.id, station])).values())
    return { data: unique, source: 'api' }
  } catch (error) {
    if (isAbortError(error)) throw error
    if (!canUseSeedFallback(error)) throw error
    return { data: cloneDeep(fallback), source: 'seed', warning: toDisplayError(error) }
  }
}

export async function getStationById(stationId: string, signal?: AbortSignal): Promise<ApiPayloadResult<Station | undefined>> {
  const normalizedId = stationId.trim()
  if (!normalizedId) return { data: undefined, source: 'seed', warning: 'رابط المحطة غير مكتمل.' }

  try {
    const payload = await loadPayload<unknown>(`/api/stations/${encodeURIComponent(normalizedId)}`, signal)
    const station = normalizeStation(unwrapStationPayload(payload))
    if (!station) throw new Error('تعذر قراءة بيانات المحطة.')
    return { data: station, source: 'api' }
  } catch (error) {
    if (isAbortError(error)) throw error
    if (!canUseSeedFallback(error)) throw error
    return {
      data: cloneDeep(SEED_STATIONS.find((station) => station.id === normalizedId)),
      source: 'seed',
      warning: toDisplayError(error),
    }
  }
}

export function getSectionsErrorMessage(error: ApiErrorBody): string {
  return error?.message ?? error?.error ?? 'تعذّر تحميل الأقسام.'
}
